import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, PenSquare } from 'lucide-react';
import { opportunitiesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useMember } from '../lib/memberAuth';
import { BACKEND_URL as API } from '../lib/config';
import { PageHero } from './ModernPage';
import { useSettings } from '../App';
import { opportunitySkin } from '../lib/opportunitySkin';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» /opportunities (s141) — approved by Anthony: "Photo Cards", the
// original card (text over the photo) in the Moderno style, same card family
// as Moderno Companies: photo grey at rest and in colour on hover, status chip
// on the photo, type + name always visible; goal, minimum ticket, closing date
// and "View details" rise on hover (always shown on touch). Same data, type
// filter (?type=), "Develop an Opportunity" link and detail links as the
// default page. Rendered only when settings.active_theme === 'modern'.
// ─────────────────────────────────────────────────────────────────────────

const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const line = 'rgba(20,26,58,.1)';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const money = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const day = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const CSS = `
.mop-photo{filter:grayscale(1);transition:filter 400ms ease,transform 500ms cubic-bezier(.23,1,.32,1)}
.mop-card.is-closed .mop-photo{opacity:.8}
.mop-info{transform:translateY(calc(100% - 6.6rem));transition:transform 320ms cubic-bezier(.23,1,.32,1)}
.mop-more{opacity:0;transition:opacity 220ms ease}
@media (hover:none){.mop-info{transform:none}.mop-more{opacity:1}}
@media (hover:hover) and (pointer:fine){
  .mop-card:hover .mop-photo{filter:none;transform:scale(1.03)}
  .mop-card.is-closed:hover .mop-photo{filter:grayscale(.4)}
  .mop-card:hover .mop-info{transform:none}.mop-card:hover .mop-more{opacity:1}
}
.mop-card:focus-visible{outline:2px solid var(--color-heading,#1a2332);outline-offset:3px}
.mop-card:focus-visible .mop-photo{filter:none}
.mop-card:focus-visible .mop-info{transform:none}.mop-card:focus-visible .mop-more{opacity:1}
.mop-title{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
@media (prefers-reduced-motion:reduce){.mop-photo,.mop-info,.mop-more{transition:none}.mop-info{transform:none}.mop-more{opacity:1}}
`;

function Card({ o }) {
  const cover = (o.images || []).find(Boolean) || o.type_default_image;
  const open = !!o.funding_open;
  return (
    <Link to={`/opportunities/${o.slug}`} className={`mop-card group relative block overflow-hidden rounded-[var(--op-radius)] ${open ? '' : 'is-closed'}`} style={{ background: 'var(--op-dark)' }} data-testid={`opportunity-card-${o.slug}`}>
      <span className="block aspect-[5/4] overflow-hidden">
        {cover
          ? <img src={img(cover)} alt="" loading="lazy" className="mop-photo w-full h-full object-cover" />
          : <span className="w-full h-full grid place-items-center px-6 text-center text-3xl font-bold text-white/25" style={{ fontFamily: 'var(--op-head)' }}>{(o.name || '?').split(' ').slice(0, 2).join(' ')}</span>}
      </span>
      <span className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(12,13,16,.94) 0%, rgba(12,13,16,.5) 48%, rgba(12,13,16,0) 78%)' }} />
      <span className="absolute top-3.5 left-3.5 text-[11px] font-bold px-3 py-1 rounded-full"
        style={open ? { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' } : { background: 'rgba(22,23,26,.72)', color: 'rgba(255,255,255,.85)', backdropFilter: 'blur(4px)' }}>
        {open ? 'Open for funding' : 'Funding closed'}
      </span>
      <span className="mop-info absolute inset-x-0 bottom-0 p-5 flex flex-col gap-1.5 text-white">
        {o.type_name && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.14em', color: 'var(--color-accent, #ff9c1b)' }}>{o.type_name}</span>}
        <span className="mop-title font-semibold" style={{ fontFamily: 'var(--op-head)', fontSize: '1.3rem', lineHeight: 1.2 }}>{o.name}</span>
        <span className="mop-more flex flex-col gap-3 mt-2">
          <span className="flex flex-wrap gap-x-6 gap-y-1">
            {[['Goal', money(o.total_amount)], ['Min. ticket', money(o.minimum_investment_amount)], [open ? 'Closes' : 'Closed', day(o.funding_ends_at)]].map(([k, v]) => (
              <span key={k} className="flex flex-col">
                <span className="text-[10.5px] font-semibold uppercase text-white/55" style={{ letterSpacing: '.08em' }}>{k}</span>
                <span className="text-sm font-bold tabular-nums">{v}</span>
              </span>
            ))}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: 'var(--color-accent, #ff9c1b)' }}>View details <ArrowRight className="w-3.5 h-3.5" /></span>
        </span>
      </span>
    </Link>
  );
}

export default function ModernOpportunities() {
  const skin = opportunitySkin(useSettings().active_theme);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = React.useState(null);
  const [banner, setBanner] = React.useState('');
  const [types, setTypes] = React.useState([]);
  const { user } = useAuth();
  const { member } = useMember();
  const typeId = parseInt(searchParams.get('type') || '0', 10);

  React.useEffect(() => { opportunitiesAPI.types().then(r => setTypes(r.data || [])).catch(() => {}); }, []);
  React.useEffect(() => {
    setItems(null);
    opportunitiesAPI.listPublished(typeId).then(r => {
      const list = r.data || [];
      setItems(list);
      // Header photo: the first deal with a picture (kept when a filter is applied).
      setBanner(b => b || ((list.find(o => (o.images || []).find(Boolean)) || {}).images || []).find(Boolean) || '');
    }).catch(() => setItems([]));
  }, [typeId]);

  const pill = (active) => ({ borderColor: active ? 'var(--color-heading, #1a2332)' : 'rgba(20,26,58,.15)', background: active ? 'var(--color-heading, #1a2332)' : '#fff', color: active ? '#fff' : 'var(--color-heading, #1a2332)' });
  const pick = (id) => setSearchParams(id ? { type: String(id) } : {});

  return (
    <div data-testid="opportunities-page" className="bg-white" style={skin}>
      <style>{CSS}</style>
      <PageHero title="Investment Opportunities" summary="Member-authored deals and projects, published after review." banner={banner}
        trail={[{ label: 'Home', to: '/' }, { label: 'Opportunities' }]}
        badge={(user || member) ? (
          <Link to="/opportunities/develop" className="mx-btn mx-btn--secondary mx-dark order-last self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-bold text-white" style={{ borderColor: 'rgba(255,255,255,.55)' }} data-testid="develop-link">
            <PenSquare className="w-4 h-4" /> Develop an Opportunity
          </Link>
        ) : null} />
      <div className={`${shell} py-12 lg:py-16 flex flex-col gap-10`}>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by type">
            {[{ type_id: 0, name: 'All types' }, ...types].map(t => (
              <button key={t.type_id} type="button" role="tab" aria-selected={typeId === t.type_id} onClick={() => pick(t.type_id)}
                className="px-4 py-1.5 rounded-full border text-[13px] font-semibold transition-colors duration-150 active:scale-[0.97]" style={pill(typeId === t.type_id)} data-testid={`opportunity-type-${t.type_id}`}>{t.name}</button>
            ))}
        </div>
        {items === null ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[5/4] rounded-[var(--op-radius)] animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />)}</div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center border-t border-b" style={{ borderColor: line, color: 'var(--color-body-text, #475569)' }}>No published opportunities{typeId ? ' of this type' : ''} yet.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map(o => <Card key={o.id} o={o} />)}</div>
        )}
      </div>
    </div>
  );
}
